/**
 * API Client for Audio Evaluation Platform
 * 
 * This module provides a centralized Axios-based API client for all backend endpoints.
 * 
 * Route Structure:
 * - All routes are relative to API_BASE_URL
 * - If backend is mounted at /api, include it in NEXT_PUBLIC_API_URL
 *   Example: NEXT_PUBLIC_API_URL=http://localhost:8000/api
 * - If backend is at root, use: NEXT_PUBLIC_API_URL=http://localhost:8000
 * 
 * Important Endpoints:
 * - /batch/results/history - Batch history (returns batch summaries)
 * - /history - Conversation history (lightweight conversation metadata)
 * - /history/{id} - Conversation details
 * - /history/{id}/metrics - Conversation metrics
 * 
 * Debugging:
 * - In development mode, all requests/responses are logged to console
 * - Check browser console for detailed request/response information
 * - 404 errors will show the full resolved URL
 */

import axios from 'axios';

// API Base URL Configuration
// For local development, use: NEXT_PUBLIC_API_URL=http://localhost:8000
// For ngrok/production, use: NEXT_PUBLIC_API_URL=https://your-ngrok-url.ngrok-free.app
// NOTE: If using ngrok, ensure CORS_ORIGINS in backend includes your frontend URL
// 
// IMPORTANT: Next.js requires restart after changing NEXT_PUBLIC_* environment variables
// The fallback below is for development only - always set NEXT_PUBLIC_API_URL in .env.local
export const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

// Ensure baseURL doesn't end with a slash (axios handles path joining)
const normalizedBaseURL = API_BASE_URL.endsWith('/') 
  ? API_BASE_URL.slice(0, -1) 
  : API_BASE_URL;

// Validate baseURL is properly set (client-side check)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  const isNgrok = normalizedBaseURL.includes('ngrok');
  const isLocalhost = normalizedBaseURL.includes('localhost') || normalizedBaseURL.includes('127.0.0.1');
  
  console.log('[API Client] Configuration:', {
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    API_BASE_URL: API_BASE_URL,
    normalizedBaseURL: normalizedBaseURL,
    isAbsolute: normalizedBaseURL.startsWith('http'),
    isNgrok,
    isLocalhost,
  });
  
  if (!normalizedBaseURL.startsWith('http')) {
    console.error('[API Client] ERROR: baseURL is not an absolute URL! This will cause requests to fail.');
  }
  
  if (isNgrok) {
    console.warn(
      '[API Client] WARNING: Using ngrok URL. Ensure:\n' +
      '1. ngrok tunnel is running and forwarding to localhost:8000\n' +
      '2. Backend CORS_ORIGINS includes your frontend URL (http://localhost:3000)\n' +
      '3. For local development, consider using http://localhost:8000 instead'
    );
  }
}

const apiClient = axios.create({
  baseURL: normalizedBaseURL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
  timeout: 30000, // 30 second timeout
  withCredentials: false, // Set to true only if backend requires credentials
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    // Import getAuthToken dynamically to avoid circular dependencies
    if (typeof window !== 'undefined') {
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Silently fail if token can't be retrieved
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Log baseURL configuration on client side (client-side only check)
if (typeof window !== 'undefined') {
  console.log('[API Client] Initialized with baseURL:', normalizedBaseURL);
}

// Add request interceptor for debugging in development
if (process.env.NODE_ENV === 'development') {
  apiClient.interceptors.request.use(
    (config) => {
      // Ensure we're using absolute URLs
      const baseURL = config.baseURL || normalizedBaseURL;
      const requestURL = config.url || '';
      
      // Construct full URL
      const fullUrl = requestURL.startsWith('http') 
        ? requestURL 
        : `${baseURL}${requestURL.startsWith('/') ? '' : '/'}${requestURL}`;
      
      console.log(`[API Client] ${config.method?.toUpperCase()} Request:`, {
        baseURL: baseURL,
        url: requestURL,
        fullURL: fullUrl,
        params: config.params,
        data: config.data,
      });
      
      // Verify the URL is absolute (should always be with baseURL set)
      if (!fullUrl.startsWith('http')) {
        console.warn('[API Client] WARNING: Request URL is not absolute! This may cause requests to hit Next.js instead of the backend.');
      }
      
      return config;
    },
    (error) => {
      console.error('[API Client] Request error:', error);
      return Promise.reject(error);
    }
  );

  // Add response interceptor for debugging
  apiClient.interceptors.response.use(
    (response) => {
      console.log(`[API Client] Response ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
      return response;
    },
    (error) => {
      const baseURL = error.config?.baseURL || normalizedBaseURL;
      const requestURL = error.config?.url || '';
      const resolvedURL = `${baseURL}${requestURL.startsWith('/') ? '' : '/'}${requestURL}`;
      
      if (error.response) {
        // Server responded with error status
        const errorDetails: any = {
          type: 'SERVER_ERROR',
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          resolvedURL: resolvedURL,
          method: error.config?.method?.toUpperCase(),
        };
        
        // Enhanced 404 error messages
        if (error.response.status === 404) {
          errorDetails.suggestion = 
            `The endpoint ${resolvedURL} was not found. ` +
            `Verify the backend route exists and the baseURL is correct. ` +
            `Expected backend routes: /history, /batch/results/history, etc.`;
        }
        
        console.error(`[API Client] Server Error ${error.response.status}:`, errorDetails);
      } else if (error.request) {
        // Request was made but no response received - likely CORS or network issue
        const isNgrok = baseURL.includes('ngrok');
        const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'unknown';
        
        const errorDetails: any = {
          type: 'NETWORK_ERROR',
          message: 'No response received from server',
          resolvedURL: resolvedURL,
          method: error.config?.method?.toUpperCase(),
          currentOrigin: currentOrigin,
          baseURL: baseURL,
        };
        
        // Detect likely CORS issues
        if (isNgrok && typeof window !== 'undefined') {
          errorDetails.likelyCause = 'CORS';
          errorDetails.suggestion = 
            `CORS preflight likely failed. Ensure:\n` +
            `1. Backend CORS_ORIGINS includes: ${currentOrigin}\n` +
            `2. Backend is running and accessible at ${baseURL}\n` +
            `3. Check browser DevTools Network tab for OPTIONS request status\n` +
            `4. Verify backend logs show OPTIONS /history returning 200, not 400`;
        } else {
          errorDetails.likelyCause = 'NETWORK';
          errorDetails.suggestion = 
            `Network error. Check:\n` +
            `1. Backend server is running at ${baseURL}\n` +
            `2. Firewall/network is not blocking the connection\n` +
            `3. For ngrok: tunnel is active and forwarding correctly`;
        }
        
        console.error('[API Client] Network Error:', errorDetails);
      } else {
        // Error setting up request
        console.error('[API Client] Request Setup Error:', {
          type: 'SETUP_ERROR',
          message: error.message,
          resolvedURL: resolvedURL,
        });
      }
      return Promise.reject(error);
    }
  );
}

// Types
export interface BatchUploadResponse {
  batch_id: string;
  status: string;
  created_at: string;
}

export interface BatchStatusResponse {
  batch_id: string;
  status: string;
  eval_suite_id: string;
  agent_version: string;
  created_at: string;
  updated_at: string;
  total_audios: number;
  processed_audios: number;
}

export interface ScenarioDetail {
  scenario_id: string;
  verdict: string;
  severity: string;
  reasoning?: string[];
  feedback?: {
    summary: string;
    analysis: string;
    checkpoints?: Array<{
      name: string;
      status: string;
      description: string;
      evidence?: string;
    }>;
    evidence?: Record<string, any>;
    recommendations?: Array<{
      priority: string;
      action: string;
      rationale: string;
    }>;
    root_cause?: string;
    strengths?: string[];
    // New checkpoint-first format fields
    failure_summary?: string;
    failed_checkpoints?: Array<{
      name: string;
      observed_behavior: string;
      expected_behavior: string;
      severity: string;
      evidence?: string;
    }>;
    passed_checkpoints?: Array<{
      name: string;
      confirmation: string;
    }>;
  };
}

export interface TranscriptEntry {
  speaker: string; // "user" or "agent"
  text: string;
}

export interface AudioResult {
  audio_id: string;
  audio_url?: string;
  overall_score: number;
  verdict: string;
  failed_scenarios?: string[];
  successful_scenarios?: string[];
  scenario_details?: ScenarioDetail[];
  transcript?: TranscriptEntry[];
  duration_sec?: number;
  user_feedback?: string;
  user_feedback_updated_at?: string;
  user_feedback_updated_by?: string;
}

export interface BatchSummary {
  batch_id: string;
  status: string;
  average_score: number;
  pass_rate: number;
  blocker_failures: number;
  critical_failures: number;
  high_failures: number;
  total_audios: number;
  passed_audios: number;
  failed_audios: number;
  top_failing_scenarios?: Array<{
    scenario_id: string;
    failure_count: number;
  }>;
  top_passing_scenarios?: Array<{
    scenario_id: string;
    pass_count: number;
  }>;
  created_at: string;
  updated_at: string;
}

export interface BatchResultsResponse {
  batch_id: string;
  summary: BatchSummary;
  audio_results: AudioResult[];
}

// API Functions
export const api = {
  // Health check
  async health(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get('/health');
    return response.data;
  },

  // Upload batch
  async uploadBatch(
    evalSuiteId: string,
    agentVersion: string,
    files: File[]
  ): Promise<BatchUploadResponse> {
    const formData = new FormData();
    formData.append('eval_suite_id', evalSuiteId);
    formData.append('agent_version', agentVersion);
    
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await apiClient.post('/batch/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'ngrok-skip-browser-warning': 'true',
      },
    });
    return response.data;
  },

  // Get batch status
  async getBatchStatus(batchId: string): Promise<BatchStatusResponse> {
    const response = await apiClient.get(`/batch/status/${batchId}`);
    return response.data;
  },

  // Get batch summary
  async getBatchSummary(batchId: string): Promise<BatchSummary> {
    const response = await apiClient.get(`/batch/summary/${batchId}`);
    return response.data;
  },

  // Get batch results
  async getBatchResults(batchId: string): Promise<BatchResultsResponse> {
    const response = await apiClient.get(`/batch/results/${batchId}`);
    return response.data;
  },

  // Get batch history
  async getBatchHistory(params?: {
    limit?: number;
    offset?: number;
    status_filter?: string;
  }): Promise<{
    total: number;
    returned: number;
    offset: number;
    limit: number;
    batches: Array<{
      batch_id: string;
      eval_suite_id: string;
      agent_version: string;
      status: string;
      created_at: string;
      updated_at: string;
      audio_count: number;
      pass_rate: number;
      avg_score: number;
      min_score: number;
      max_score: number;
      feedback_count?: number;
      feedback_coverage?: number;
      recent_feedback_count?: number;
    }>;
  }> {
    const response = await apiClient.get('/batch/results/history', { params });
    return response.data;
  },

  // Optimized history endpoints
  // Note: This endpoint uses /history (lightweight conversation metadata)
  // If you need batch history, use getBatchHistory() which calls /batch/results/history
  // IMPORTANT: If /history returns 404, ensure the history_router is properly included in the backend
  async getHistory(params?: {
    limit?: number;
    offset?: number;
    status_filter?: string;
  }): Promise<{
    conversations: Array<{
      id: string;
      audio_id: string;
      batch_id: string;
      name: string | null;
      status: string;
      created_at: string;
      score: number;
      duration: number | null;
      eval_suite_id?: string | null;
      agent_version?: string | null;
    }>;
    total: number;
    limit: number;
    offset: number;
    has_next: boolean;
    has_previous: boolean;
  }> {
    try {
      const response = await apiClient.get('/history', { params });
      return response.data;
    } catch (error: any) {
      // Enhanced error logging for debugging
      const baseURL = apiClient.defaults.baseURL || API_BASE_URL;
      
      if (error.response?.status === 404) {
        console.error(
          `[API Client] /history endpoint returned 404. ` +
          `Please verify:\n` +
          `1. The history_router is included in backend/app/api/routes.py (line 3829)\n` +
          `2. The backend server is running and accessible at ${baseURL}\n` +
          `3. Check backend logs for routing errors\n` +
          `Attempted URL: ${baseURL}/history`
        );
      } else if (!error.response && error.request) {
        // Network error - request was made but no response received
        // This is handled by the response interceptor above, but keeping for backward compatibility
        const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'unknown';
        const isNgrok = baseURL.includes('ngrok');
        
        console.error(
          `[API Client] Network Error: Request to ${baseURL}/history failed.\n` +
          `Current origin: ${currentOrigin}\n` +
          `Possible causes:\n` +
          (isNgrok 
            ? `1. CORS preflight failed: Backend CORS_ORIGINS must include ${currentOrigin}\n` +
              `2. ngrok tunnel is not running or not forwarding to localhost:8000\n` +
              `3. ngrok URL is incorrect or expired\n` +
              `4. Check browser DevTools: OPTIONS /history should return 200, not 400\n`
            : `1. Backend server is not running on ${baseURL}\n` +
              `2. CORS issue: Backend CORS_ORIGINS must include ${currentOrigin}\n` +
              `3. Firewall or network issue blocking the connection\n`
          ) +
          `\nTo fix:\n` +
          `- Set backend CORS_ORIGINS environment variable to include ${currentOrigin}\n` +
          `- For local development, set NEXT_PUBLIC_API_URL=http://localhost:8000 in .env.local\n` +
          `- Ensure backend is running and restart after changing CORS_ORIGINS\n` +
          `- Check backend logs for CORS configuration on startup`
        );
      }
      throw error;
    }
  },

  async getConversationDetail(conversationId: string): Promise<{
    id: string;
    audio_id: string;
    batch_id: string;
    transcript?: TranscriptEntry[] | null;
    scenario_details?: ScenarioDetail[] | null;
    feedback?: string | null;
    overall_score: number;
    verdict: string;
    duration_sec?: number | null;
    created_at: string;
    eval_suite_id?: string | null;
    agent_version?: string | null;
  }> {
    const response = await apiClient.get(`/history/${conversationId}`);
    return response.data;
  },

  async getConversationMetrics(conversationId: string): Promise<{
    audio_id: string;
    overall_score: number;
    verdict: string;
    failed_scenarios?: string[] | null;
    successful_scenarios?: string[] | null;
    duration_sec?: number | null;
    scenario_count: number;
    blocker_failures: number;
    critical_failures: number;
    high_failures: number;
  }> {
    const response = await apiClient.get(`/history/${conversationId}/metrics`);
    return response.data;
  },

  // Export batch results
  async exportBatchResults(
    batchId: string,
    format: 'json' | 'csv' = 'json'
  ): Promise<Blob> {
    const response = await apiClient.get(
      `/batch/results/export/${batchId}`,
      {
        params: { format },
        responseType: 'blob',
      }
    );
    return response.data;
  },

  // Update user feedback for an audio result
  async updateUserFeedback(
    audioId: string,
    feedback: string
  ): Promise<AudioResult> {
    const response = await apiClient.put(
      `/batch/audio/${audioId}/feedback`,
      {
        user_feedback: feedback,
      }
    );
    return response.data;
  },

  // Download audio file
  async downloadAudio(audioId: string): Promise<Blob> {
    const response = await apiClient.get(
      `/batch/audio/${audioId}`,
      {
        params: { download: true },
        responseType: 'blob',
      }
    );
    return response.data;
  },

  // Download audio result as JSON
  async downloadAudioResult(audioId: string): Promise<Blob> {
    const response = await apiClient.get(
      `/batch/audio/${audioId}/result`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  // ===== Coval-style Features =====

  // Resimulation from transcripts
  async resimulateFromTranscripts(
    batchId: string,
    scenarioIds?: string[]
  ): Promise<{
    batch_id: string;
    resimulated_count: number;
    error_count: number;
    total_calls: number;
    results: Array<{
      call_id: string;
      passed: number;
      failed: number;
      blocker_failures: number;
      total_scenarios: number;
    }>;
    errors: Array<{ call_id: string; error: string }>;
  }> {
    const response = await apiClient.post(
      `/batch/resimulate/${batchId}`,
      { scenario_ids: scenarioIds }
    );
    return response.data;
  },

  // Observability - Live Call Monitoring
  async startLiveCallMonitoring(
    callId: string,
    tenantId: string,
    agentId: string
  ): Promise<{
    monitor_id: string;
    call_id: string;
    status: string;
    kpis?: Record<string, any>;
  }> {
    const response = await apiClient.post('/observability/live-call/start', {
      call_id: callId,
      tenant_id: tenantId,
      agent_id: agentId,
    });
    return response.data;
  },

  async updateLiveCallTranscript(
    callId: string,
    transcript: Array<{ speaker: string; text: string; timestamp?: number }>,
    metadata?: Record<string, any>
  ): Promise<{
    monitor_id: string;
    call_id: string;
    status: string;
    kpis?: Record<string, any>;
  }> {
    const response = await apiClient.post(
      `/observability/live-call/${callId}/transcript`,
      { transcript, metadata }
    );
    return response.data;
  },

  async completeLiveCallMonitoring(
    callId: string
  ): Promise<{
    monitor_id: string;
    call_id: string;
    status: string;
    kpis?: Record<string, any>;
  }> {
    const response = await apiClient.post(
      `/observability/live-call/${callId}/complete`
    );
    return response.data;
  },

  async getLiveCalls(params?: {
    tenant_id?: string;
    agent_id?: string;
    status_filter?: string;
    hours?: number;
    limit?: number;
  }): Promise<Array<{
    monitor_id: string;
    call_id: string;
    status: string;
    kpis?: Record<string, any>;
  }>> {
    const response = await apiClient.get('/observability/live-calls', { params });
    return response.data;
  },

  async getAggregateKPIs(params?: {
    tenant_id?: string;
    agent_id?: string;
    hours?: number;
  }): Promise<{
    time_range_hours: number;
    total_calls: number;
    failed_intents: number;
    blocker_failures: number;
    escalation_rate: number;
    average_latency_ms: number;
  }> {
    const response = await apiClient.get('/observability/kpis/aggregate', { params });
    return response.data;
  },

  async getLiveCallDetail(callId: string): Promise<{
    monitor_id: string;
    call_id: string;
    tenant_id: string;
    agent_id: string;
    status: string;
    started_at?: string;
    completed_at?: string;
    transcript?: Array<{ speaker: string; text: string }>;
    metadata?: Record<string, any>;
    kpis?: Record<string, any>;
  }> {
    const response = await apiClient.get(`/observability/live-call/${callId}`);
    return response.data;
  },

  // Simulation - Stress Testing
  async runStressTest(
    batchId: string,
    numConcurrent: number = 50,
    totalSimulations: number = 1000,
    scenarioIds?: string[]
  ): Promise<{
    batch_id: string;
    total_simulations: number;
    concurrent: number;
    processed: number;
    errors: number;
    passed: number;
    failed: number;
    blocker_failures: number;
    pass_rate: number;
    completed_at: string;
    error_details: string[];
  }> {
    const response = await apiClient.post('/simulation/stress-test', {
      batch_id: batchId,
      num_concurrent: numConcurrent,
      total_simulations: totalSimulations,
      scenario_ids: scenarioIds,
    });
    return response.data;
  },

  // CI/CD - Regression Testing
  async createRegressionSuite(
    tenantId: string,
    agentId: string,
    name: string,
    scenarioIds: string[],
    options?: {
      description?: string;
      baselineBatchId?: string;
      webhookUrl?: string;
    }
  ): Promise<{
    suite_id: string;
    tenant_id: string;
    agent_id: string;
    name: string;
    description?: string;
    scenario_ids: string[];
    baseline_batch_id?: string;
    webhook_url?: string;
    status: string;
    created_at: string;
  }> {
    const response = await apiClient.post('/cicd/regression-suite', {
      tenant_id: tenantId,
      agent_id: agentId,
      name,
      scenario_ids: scenarioIds,
      description: options?.description,
      baseline_batch_id: options?.baselineBatchId,
      webhook_url: options?.webhookUrl,
    });
    return response.data;
  },

  async runRegressionTest(
    suiteId: string,
    newBatchId: string
  ): Promise<{
    suite_id: string;
    regression_detected: boolean;
    baseline: {
      average_score: number | null;
      pass_rate: number | null;
    };
    new: {
      average_score: number;
      pass_rate: number;
    };
    comparison: Record<string, any>;
    webhook_sent: boolean;
  }> {
    const response = await apiClient.post('/cicd/regression-test/run', {
      suite_id: suiteId,
      new_batch_id: newBatchId,
    });
    return response.data;
  },

  async getRegressionSuite(suiteId: string): Promise<{
    suite_id: string;
    tenant_id: string;
    agent_id: string;
    name: string;
    description?: string;
    scenario_ids: string[];
    baseline_batch_id?: string;
    webhook_url?: string;
    status: string;
    created_at: string;
  }> {
    const response = await apiClient.get(`/cicd/regression-suite/${suiteId}`);
    return response.data;
  },

  // Authentication endpoints
  async login(email: string, password: string): Promise<{
    access_token: string;
    token_type: string;
    user: {
      id: string;
      email: string;
      full_name: string | null;
      is_admin: boolean;
    };
  }> {
    const response = await apiClient.post('/auth/login', {
      email,
      password,
    });
    return response.data;
  },

  async register(email: string, password: string, fullName?: string): Promise<{
    id: string;
    email: string;
    full_name: string | null;
    created_at: string;
  }> {
    const response = await apiClient.post('/auth/register', {
      email,
      password,
      full_name: fullName,
    });
    return response.data;
  },
};

export default api;

